import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import './preLoginSettings.css';

/** Legacy `SL_CopyNoOfSamples` — one field, NoOfSamples, default 1. */
export function CopySamplesDialog({ sampleId, onClose, onOk }: {
  sampleId: string | null;
  onClose: () => void;
  onOk: (count: number) => void;
}) {
  const notify = useNotify();
  const [count, setCount] = useState(1);

  const submit = () => {
    if (!Number.isInteger(count) || count < 1) { notify.error('Number of samples must be greater than 0.'); return; }
    if (count > 500) { notify.error('Number of samples must not exceed 500.'); return; }
    onOk(count);
  };

  return (
    <AppDialog
      open onClose={onClose} title="Copy Samples" icon="ti-copy" width={420}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={submit}><i className="ti ti-check" /> OK</button>
      </>}
    >
      <div className="pl-add-grid" style={{ gridTemplateColumns: 'auto 1fr' }}>
        <label>Copy from</label>
        <input readOnly value={sampleId ?? ''} />
        <label className="req">No Of Samples</label>
        <input type="number" min={1} max={500} value={count}
               onChange={e => setCount(Number(e.target.value))} />
      </div>
    </AppDialog>
  );
}

export interface PickRow {
  oid: string;
  sampleID: string | null;
  sampleName: string | null;
  sampleMatrix: string | null;
}

/** Multi-select of target samples for Copy Test / Copy Bottles. */
export function TargetSamplesDialog({ title, note, rows, loading, emptyText, okLabel, onClose, onOk }: {
  title: string;
  note: string;
  rows: PickRow[];
  loading: boolean;
  emptyText: string;
  okLabel: string;
  onClose: () => void;
  onOk: (oids: string[]) => void;
}) {
  const notify = useNotify();
  const [picked, setPicked] = useState<Set<string | number>>(new Set());
  useEffect(() => { setPicked(new Set()); }, [rows]);

  const cols: AppColumn<PickRow>[] = [
    { field: 'sampleID',     header: 'SampleID',     width: 130 },
    { field: 'sampleName',   header: 'SampleName',   width: 150 },
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 130 },
  ];

  const submit = () => {
    if (picked.size === 0) { notify.warning(note); return; }
    onOk([...picked].map(String));
  };

  return (
    <AppDialog
      open onClose={onClose} title={title} icon="ti-copy" width={560}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={submit}><i className="ti ti-check" /> {okLabel}</button>
      </>}
    >
      <AppDataGrid<PickRow>
        rows={rows}
        columns={cols}
        getRowId={r => r.oid}
        loading={loading}
        selectable
        selectedIds={picked}
        onSelectionChange={setPicked}
        defaultPageSize={10}
        emptyText={emptyText}
      />
    </AppDialog>
  );
}
